import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Article — "Your best operator is about to retire: capturing tribal knowledge".
 *
 * Author's intended goal: "Knowledge" (turning expertise into operating memory).
 * The published `ArticleGoal` union is currently Educational | ROI | Technical,
 * so this is filed under "Educational" — it is a plain-language primer on the
 * problem and the fix. If a "Knowledge" goal is added later, switch `goal`.
 *
 * Honest, vendor-neutral body; the product appears only in the closing block.
 * Every figure is illustrative and reads as a generic range.
 */
export const CAPTURING_TRIBAL_KNOWLEDGE_ARTICLE: Article = {
  slug: "capturing-tribal-knowledge",
  title: "Your best operator is about to retire: capturing tribal knowledge",
  deck:
    "The most valuable asset in many foundries is not in the asset register. It is in the head of the furnace operator who can hear when a melt is wrong and the metallurgist who knows which adjustment fixes which season. When they leave, it leaves with them — unless you have been writing it down.",
  goal: "Educational",
  readingMins: 8,
  heroStat: { value: "30+ yrs", label: "of judgement that walks out the door with one retirement" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "Every foundry has one: the operator who can stand at the furnace, glance at the tap, and say 'hold it, the carbon's low' before the spectrometer confirms it. The one who knows that this pattern always needs a touch more venting in humid weather, that the muller runs dry on Monday mornings after a weekend idle, that a particular customer's casting is fussy about pour rate. None of this is written anywhere. It lives as instinct, built over decades, and it quietly keeps the plant out of trouble. It is also, in the most literal sense, a single point of failure with a retirement date.",
    },
    {
      kind: "paragraph",
      text:
        "We call it tribal knowledge because it is transmitted the way tribes transmit lore: by being there, watching, and absorbing. That worked when people stayed thirty years and apprentices shadowed masters for a decade. It works far less well now, when experienced people retire in clusters and their replacements are expected to be productive in months, not years. The knowledge did not become less valuable. The mechanism for passing it on simply broke.",
    },
    {
      kind: "statCallout",
      value: "~70%",
      label: "of what an expert operator knows is never written down anywhere",
      caption:
        "Illustrative figure, widely echoed across process industries. The point is directional: the formal procedures capture the routine; the hard-won exceptions live only in people's heads.",
      tone: "danger",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The cost",
      heading: "What actually walks out with a retirement",
      sub: "It is not the documented procedure that you lose. It is the judgement about when the procedure does not apply.",
    },
    {
      kind: "paragraph",
      text:
        "When a thirty-year operator leaves, the standard operating procedures stay on the shelf, so it is tempting to think the loss is contained. It is not, because procedures describe the normal case and the value of an expert is in the abnormal one. The new operator can follow the SOP perfectly and still not know that this particular alloy behaves differently in winter, or that a faint smell off the cupola means a specific problem, or which of three plausible fixes actually works for a recurring defect. That gap shows up as a slow, expensive drift: scrap creeps up, the same problems get re-solved from scratch, and decisions that used to take seconds now take a meeting.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: how a plant degrades in the year after losing a key expert",
      unit: "% change",
      bars: [
        { label: "Scrap rate (rises)", value: 22, tone: "danger", display: "+22%" },
        { label: "Time to diagnose a defect (rises)", value: 40, tone: "warning", display: "+40%" },
        { label: "Repeat incidents (rise)", value: 30, tone: "warning", display: "+30%" },
        { label: "First-time-right on new grades (falls)", value: 18, tone: "info", display: "-18%" },
      ],
      caption:
        "Generic, illustrative figures. The pattern is consistent across shops: the losses are diffuse and rarely attributed to the departure that caused them.",
    },
    {
      kind: "pullQuote",
      quote:
        "The procedure tells the new operator what to do on a normal day. The retiring expert was the one who knew which days were not normal — and that is exactly the part nobody wrote down.",
      attribution: "The real shape of knowledge loss",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The fix that does not work",
      heading: "Why 'just document everything' fails",
    },
    {
      kind: "paragraph",
      text:
        "The instinctive response is to sit the expert down and have them write it all up before they go. This almost never works, for two reasons. First, expertise is largely tacit — people genuinely cannot articulate rules they apply by feel, and a fat binder of generalities is not the same as knowing what to do at 2am with a bad heat. Second, a document written once is frozen and goes stale, while the knowledge that matters keeps evolving with new grades, new customers and new equipment. A static manual captures a snapshot of yesterday's expert. It does not capture the living, updating judgement you are actually trying to keep.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "The exit-interview illusion",
      text:
        "A week of debriefs before someone retires feels like capture but mostly produces platitudes. Real expertise surfaces in context — at the moment of a decision, on a specific heat — not in a conference room trying to summarise thirty years from memory.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The fix that does",
      heading: "Capture the intervention, not the lecture",
    },
    {
      kind: "paragraph",
      text:
        "The durable approach inverts the problem. Instead of asking experts to summarise everything in the abstract, you capture their decisions in the moment, against the actual data, as they happen. When the metallurgist adjusts an inoculant because porosity is trending up, that intervention is recorded next to the conditions that prompted it and the outcome that followed. Do this consistently and the plant slowly accumulates an operating memory: a searchable record of 'when the process looked like this, here is what we did, and here is whether it worked'. The expert is not writing a manual. They are leaving a trail, one decision at a time, and the trail is tied to real conditions rather than to fading recollection.",
    },
    {
      kind: "processFlow",
      title: "From a private instinct to a shared memory",
      steps: [
        {
          title: "Notice the condition",
          detail: "The process drifts — porosity climbs, sand runs wet — and the data shows it, not just the operator's gut.",
          icon: "gauge",
        },
        {
          title: "Record the intervention",
          detail: "The action taken is logged against the live conditions and the heat it applied to — a note in context, not a memo.",
          icon: "database",
        },
        {
          title: "Capture the outcome",
          detail: "Whether the fix worked is recorded too, so the entry is a result, not just an opinion.",
          icon: "lineChart",
        },
        {
          title: "Make it searchable",
          detail: "The next person facing the same pattern can find what worked before, by symptom — expertise on demand.",
          icon: "search",
        },
      ],
      caption:
        "Knowledge captured as a stream of contextual decisions ages gracefully — it updates itself every time someone acts.",
    },
    {
      kind: "chart",
      chart: "area",
      title: "Illustrative: an operating memory grows as interventions accumulate",
      xKey: "month",
      series: [{ dataKey: "entries", label: "Searchable interventions captured", tone: "brand" }],
      data: [
        { month: "M1", entries: 18 },
        { month: "M3", entries: 70 },
        { month: "M6", entries: 165 },
        { month: "M9", entries: 290 },
        { month: "M12", entries: 440 },
      ],
      caption:
        "Generic, illustrative ramp. Unlike a manual written once, this asset compounds — every shift adds to it, and it never goes out of date all at once.",
    },
    {
      kind: "paragraph",
      text:
        "There is a quieter benefit beyond succession. An operating memory shortens the learning curve for everyone, not just replacements for retirees. A two-year operator gains access to the accumulated judgement of every operator before them, searchable by symptom. The plant stops re-solving the same problems and starts standing on its own history. And when an expert does finally retire, the loss is real but survivable, because the most valuable part of what they knew — what they did, when, and whether it worked — is still in the building.",
    },
    {
      kind: "statCallout",
      value: "months → weeks",
      label: "the time for a new operator to reach competence on a captured process",
      caption:
        "Illustrative, not a guarantee. When the accumulated judgement is searchable, ramp-up shifts from osmosis-over-years to look-it-up-in-context.",
      tone: "success",
    },
    {
      kind: "paragraph",
      text:
        "The retirement wave is not a future risk for most foundries; it is already arriving. The plants that come through it intact will not be the ones with the thickest binders. They will be the ones that turned their experts' daily decisions into a living, searchable memory — quietly, one intervention at a time, long before the leaving party.",
    },
    {
      kind: "sahasEnables",
      intro:
        "Capturing tribal knowledge as it happens is a discipline any plant can adopt — the obstacle is having somewhere it accrues against real conditions. That is the role the platform plays.",
      capabilities: [
        {
          title: "Decisions in context",
          detail:
            "Interventions and notes attach to the live conditions and the specific heat that prompted them, so knowledge is captured at the moment it is exercised.",
          icon: "database",
        },
        {
          title: "A searchable operating memory",
          detail:
            "Past situations are findable by symptom — 'when the process looked like this, here is what we did and whether it worked' — turning history into guidance.",
          icon: "search",
        },
        {
          title: "An AI Casting Consultant over your own history",
          detail:
            "An assistant draws on the plant's accumulated record to surface what worked last time, putting decades of judgement within reach of the newest hire.",
          icon: "sparkles",
        },
      ],
    },
  ],
};
